/**
 * The sources VoteMate reads when it answers — the "connectors" behind chat.
 *
 * Surfaced in two places: the Sources button in the composer and the Sources
 * card in Settings. Most entries are `alwaysOn`: stores the research agents
 * can draw on every turn and the voter cannot switch off, shown with a green
 * "On" chip. An entry with
 * `alwaysOn: false` is optional — the voter can switch it off, and the chat
 * request then names it in `disabled_connectors` (see
 * `useSourceConnectorPrefs`). USAFacts is the first.
 *
 * Kept identical to the mobile client's `constants/sourceConnectors.ts` — the
 * same list, in the same order, with the same marks — so a voter moving between
 * a browser and the app sees one answer to "what does VoteMate read?".
 */
export const SOURCE_CONNECTOR_IDS = [
  'ballotpedia',
  'democracyWorks',
  'votemateProfiles',
  'congressionalRecord',
  'webNews',
  'usafacts',
] as const;

export type SourceConnectorId = (typeof SOURCE_CONNECTOR_IDS)[number];

/**
 * How a connector is drawn. A site favicon where the source is a single
 * public site (genuine provenance, the same mark the citation chips show); the
 * VoteMate logo for our own data; the bundled USAFacts logo for USAFacts; a glyph
 * for the kind of work where no single publisher is the source. Mirrors the rules
 * `ToolCategoryIcon` encodes for the research trail, so the mark beside a source
 * here matches the badge beside the step that read it.
 */
export type SourceConnectorMark =
  | { kind: 'votemate' }
  | { kind: 'usafacts' }
  | { kind: 'favicon'; host: string; fallbackGlyph: SourceConnectorGlyph }
  | { kind: 'glyph'; glyph: SourceConnectorGlyph };

export type SourceConnectorGlyph = 'book' | 'ballot' | 'capitol' | 'globe' | 'chart';

export interface SourceConnector {
  id: SourceConnectorId;
  mark: SourceConnectorMark;
  /** Always available to the research agents; shown with an "On" chip and no switch. */
  alwaysOn: boolean;
}

export const SOURCE_CONNECTORS: readonly SourceConnector[] = [
  {
    id: 'ballotpedia',
    mark: { kind: 'favicon', host: 'ballotpedia.org', fallbackGlyph: 'book' },
    alwaysOn: true,
  },
  {
    id: 'democracyWorks',
    mark: { kind: 'favicon', host: 'democracy.works', fallbackGlyph: 'ballot' },
    alwaysOn: true,
  },
  { id: 'votemateProfiles', mark: { kind: 'votemate' }, alwaysOn: true },
  {
    id: 'congressionalRecord',
    mark: { kind: 'favicon', host: 'congress.gov', fallbackGlyph: 'capitol' },
    alwaysOn: true,
  },
  { id: 'webNews', mark: { kind: 'glyph', glyph: 'globe' }, alwaysOn: true },
  { id: 'usafacts', mark: { kind: 'usafacts' }, alwaysOn: false },
];

/**
 * The connectors a voter can switch off, by the id the backend knows them by
 * (`maf/connectors.py` → `KNOWN_CONNECTORS`). The client id and the wire id
 * happen to match today; this map is where they would diverge.
 */
export const OPTIONAL_CONNECTOR_WIRE_IDS: Partial<Record<SourceConnectorId, string>> = {
  usafacts: 'usafacts',
};

export const OPTIONAL_SOURCE_CONNECTOR_IDS: readonly SourceConnectorId[] = SOURCE_CONNECTORS.filter(
  (c) => !c.alwaysOn
).map((c) => c.id);

/** How many connectors are on, given the ones the voter switched off. */
export const enabledConnectorCount = (disabled: readonly SourceConnectorId[]): number =>
  SOURCE_CONNECTORS.filter((c) => c.alwaysOn || !disabled.includes(c.id)).length;

/** Parse a stored value, keeping only known optional ids, deduped and sorted. */
export function parseDisabledConnectors(raw: string | null | undefined): SourceConnectorId[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return OPTIONAL_SOURCE_CONNECTOR_IDS.filter((id) => value.includes(id));
  } catch {
    return [];
  }
}

/** The disabled list after switching one connector on or off. */
export function withConnectorEnabled(
  disabled: readonly SourceConnectorId[],
  id: SourceConnectorId,
  enabled: boolean
): SourceConnectorId[] {
  if (!OPTIONAL_SOURCE_CONNECTOR_IDS.includes(id)) return [...disabled];
  const next = new Set(disabled);
  if (enabled) next.delete(id);
  else next.add(id);
  return OPTIONAL_SOURCE_CONNECTOR_IDS.filter((c) => next.has(c));
}

/**
 * The `disabled_connectors` value for a chat request, or `undefined` to omit
 * the field. Omitting it when nothing is off keeps the default request exactly
 * what it was before the switch existed.
 */
export function disabledConnectorsForRequest(
  disabled: readonly SourceConnectorId[]
): string[] | undefined {
  const ids = disabled
    .map((id) => OPTIONAL_CONNECTOR_WIRE_IDS[id])
    .filter((id): id is string => Boolean(id));
  return ids.length ? ids : undefined;
}

/** i18n keys (namespace `common`) for a connector's name and one-line description. */
export const connectorNameKey = (id: SourceConnectorId) => `sourceConnectors.connectors.${id}.name`;
export const connectorDescriptionKey = (id: SourceConnectorId) =>
  `sourceConnectors.connectors.${id}.description`;
